import {
  LoadingOutlined,
  PlusOutlined,
  CloudUploadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  DeleteOutlined
} from '@ant-design/icons';

import { useState } from 'react';

import { Card, Space, Tag, Typography, Upload, message, Alert, Modal } from 'antd';

import { DownloadViaLink, getError } from '../../clientUtils';

const { Text, Link, Title } = Typography;

const ICON_STYLES = { fontSize: '24px' };
const CARD_STYLES = { width: 168 };

const { confirm } = Modal;

export default function CustomUpload({
  title,
  description,
  sampleDownloadLink,
  sampleDownloadText,
  file,
  fileName,
  fileType,
  handleRemove,
  validator,
  onUpload,
  multiple,
  required
}) {
  const [hover, setHover] = useState(false);
  const [loading, setLoading] = useState(false);
  const { link } = file || {};

  const onMouseEnter = () => setHover(true);

  const onMouseLeave = () => setHover(false);

  const showDeleteConfirm = () => {
    confirm({
      title: 'Delete File?',
      icon: <DeleteOutlined className="light-red" />,
      className: 'delete-confirmation-modal',
      content: 'Are you sure you want to delete this file? All the list data will be lost.',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk() {
        handleRemove(file);
      },
      onCancel() {
        console.log('Cancel');
      }
    });
  };

  const handleLinkClick = () => {
    const link = sampleDownloadLink;
    const fileName = 'sample';
    DownloadViaLink(link, fileName);
  };

  const beforeUpload = function (file) {
    const upload = async (file) => {
      setLoading(true);
      try {
        validator && validator(file);
        await onUpload(file);
      } catch (error) {
        message.error(getError(error));
      } finally {
        setLoading(false);
      }
    };

    upload(file);
    return false;
  };

  const uploaderProps = {
    beforeUpload,
    accept: fileType,
    showUploadList: false,
    disabled: loading,
    listType: 'picture-card'
  };

  const uploadView = (
    <Upload {...uploaderProps}>
      <div>
        {loading ? <LoadingOutlined /> : <PlusOutlined />}
        <div>Upload</div>
      </div>
    </Upload>
  );

  const hoverView = (
    <Space align="center" size="large">
      <Link className="accent-color" href={link}>
        <DownloadOutlined style={{ ...ICON_STYLES }} />
      </Link>
      <DeleteOutlined className="light-red" onClick={showDeleteConfirm} style={{ ...ICON_STYLES }} />
    </Space>
  );

  const fileView = (
    <Card
      className="center"
      hoverable
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ ...CARD_STYLES }}
    >
      <Space align="center" direction="vertical">
        {!hover ? <FileTextOutlined style={{ ...ICON_STYLES }} /> : null}
        {hover ? hoverView : null}
        <Text ellipsis style={{ maxWidth: 150 }}>
          {fileName}
        </Text>
      </Space>
    </Card>
  );

  return (
    <div>
      <Title className={required ? 'required-after' : ''} level={3}>
        {title}
      </Title>
      <Space direction="vertical">
        <Space>
          <Text>{description}</Text>
          <Text className="pointer fw-500" onClick={handleLinkClick} underline>
            {sampleDownloadText}
          </Text>
        </Space>
        {!link || multiple ? uploadView : null}
        {link ? fileView : null}
        {file?.messages?.failure?.length ? (
          <Space direction="vertical">
            {file?.messages?.failure?.map((error) => (
              <Alert closable message={error} showIcon type="error" />
            ))}
          </Space>
        ) : null}
        {link && fileType?.includes('csv') ? <ResponseBanner file={file} /> : null}
      </Space>
    </div>
  );
}

export const ResponseBanner = ({ file }) => {
  const { summary, link } = file || {};
  const { failure, warning, success } = summary || {};
  const total = failure + warning + success;

  const tags = [
    {
      color: '#F4F6F9',
      icon: <CloudUploadOutlined style={{ fontSize: 16 }} />,
      number: total,
      text: 'Total'
    },
    {
      color: '#F5FCF1',
      icon: <CheckCircleOutlined className="success_icon" style={{ fontSize: 16 }} />,
      number: success,
      text: 'Passed'
    },
    {
      color: '#FFF8E5',
      icon: <WarningOutlined className="warning_icon" style={{ fontSize: 16 }} />,
      number: warning,
      text: 'Warnings'
    },
    {
      color: '#FFECEC',
      icon: <CloseCircleOutlined style={{ fontSize: 16 }} />,
      number: failure,
      text: 'Errors'
    }
  ];

  const getMessage = () => {
    let message = '';
    if (failure) {
      message = 'correct errors and upload again';
      return message;
    }
    if (warning) {
      message = 'view SKU Details and warnings';
      return message;
    }
    return 'view SKU Details';
  };

  return (
    <Space direction="vertical" size="middle">
      <Space>
        {tags.map((tag) =>
          tag.number > 0 ? (
            <Tag className="status_tags" color={tag?.color} size="large">
              <Space className="upload-info" size="large">
                <span className="center gap-5">
                  {tag.icon} <Text>{tag.text}</Text>
                </span>
                <span>{tag.number}</span>
              </Space>
            </Tag>
          ) : null
        )}
      </Space>
      <Space>
        <Text>
          <Link className="accent-color" href={link}>
            Download File
          </Link>{' '}
          to {getMessage()}
        </Text>
      </Space>
    </Space>
  );
};
